// Paso 5 del asistente (PE-A3b): plan de acción. Las obligatorias salen de los cálculos (no cumple, punto de
// encuentro no apto o moderado o peor, vulnerabilidad alta o muy alta) con su clave de origen: no se
// duplican y, si el origen deja de fallar, quedan "origen resuelto". También hay acciones manuales.
// Todas cuentan como evidencia de PO-18; el puntaje del Checklist no cambia en este bloque.
// PE-A4: cada acción guardada lleva sus evidencias (fotos o PDF, M37). Manager y admin adjuntan, solo admin borra,
// viewer solo mira. Una obligatoria no se cierra sin evidencia.

import React, { useEffect, useState } from 'react';
import {
  AccionEditable, editableDesdeDerivada, editableDesdeFila, ESTADOS_ACCION, ETIQUETA_ESTADO_ACCION, EstadoAccion, FilaAccion, ITEM_PO18,
  MENSAJE_CIERRE_SIN_EVIDENCIA, origenResuelto, sincronizarAcciones, validarAccion,
} from '../../lib/pe/pe-acciones';
import { ACEPTA_EVIDENCIA, FilaEvidencia, tamanoLegible } from '../../lib/pe/pe-evidencias';
import type { AccionDerivada } from '../../lib/pe/pe-paso3';
import { Campo } from './ComponentesPE';

export interface PropsPaso5 {
  acciones: FilaAccion[];
  derivadas: AccionDerivada[];
  trabajadores: { id: string; full_name: string }[];
  ro: boolean;
  puedeBorrar: boolean;
  guardando: boolean;
  onGuardar: (a: AccionEditable) => Promise<boolean>;
  onBorrar: (f: FilaAccion) => void;
  // PE-A4 (M37)
  evidencias: FilaEvidencia[];
  errorEvidencias?: string;
  onSubirEvidencia: (f: FilaAccion, archivo: File) => Promise<void>;
  onBorrarEvidencia: (e: FilaEvidencia) => void;
  onAbrirEvidencia: (e: FilaEvidencia) => Promise<void>;
}

function Evidencias({ fila, lista, ro, puedeBorrar, p }: { fila: FilaAccion; lista: FilaEvidencia[]; ro: boolean; puedeBorrar: boolean; p: PropsPaso5 }) {
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');
  const conLegado = !!fila.evidence_path?.trim();
  const subir = async (archivo: File | undefined) => {
    if (!archivo) return;
    setSubiendo(true); setError('');
    try { await p.onSubirEvidencia(fila, archivo); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setSubiendo(false); }
  };
  const abrir = async (e: FilaEvidencia) => {
    setError('');
    try { await p.onAbrirEvidencia(e); } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
  };
  return (
    <div className="pe2-evidencias" aria-label="Evidencias de la acción">
      <b>Evidencias ({lista.length + (conLegado ? 1 : 0)})</b>
      {lista.length === 0 && !conLegado && (
        <p className="pe2-nota" style={{ margin: 0 }}>
          {fila.is_mandatory ? 'Sin evidencia: para cerrarla hace falta al menos una foto o un PDF.' : 'Sin evidencia.'}
        </p>
      )}
      {(lista.length > 0 || conLegado) && (
        <ul className="pe2-evidencias-lista">
          {conLegado && <li><span>Evidencia anterior</span></li>}
          {lista.map(e => (
            <li key={e.id}>
              <span className="pe2-evidencia-nombre" title={e.file_name}>{e.file_name}</span>
              <small>{e.mime_type === 'application/pdf' ? 'PDF' : 'Foto'} · {tamanoLegible(e.size_bytes)}</small>
              <span className="pe2-evidencia-botones">
                <button type="button" className="pe2-btn" onClick={() => abrir(e)}>Abrir</button>
                {puedeBorrar && <button type="button" className="pe2-btn peligro" onClick={() => p.onBorrarEvidencia(e)}>Borrar</button>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {fila.status === 'cerrada' && fila.is_mandatory && lista.length === 0 && !conLegado && (
        <div className="pe2-error" role="status">Cerrada sin evidencia: para PO-18 cuenta como abierta.</div>
      )}
      {!ro && (
        <label className="pe2-btn pe2-btn-archivo">
          {subiendo ? 'Subiendo…' : 'Adjuntar evidencia'}
          <input type="file" accept={ACEPTA_EVIDENCIA} disabled={subiendo}
            onChange={e => { const a = e.target.files?.[0]; e.target.value = ''; subir(a); }} />
        </label>
      )}
      {!ro && <small className="pe2-nota">JPG, PNG, WEBP o PDF, hasta 10 MB.</small>}
      {error && <div className="pe2-error" role="alert">{error}</div>}
    </div>
  );
}

const nuevaManual = (): AccionEditable => ({
  sourceKey: null, sourceMethod: null, sectorId: null, norma: null, articulo: null, descripcion: '', obligatoria: false,
  responsableId: null, responsableCargo: '', fecha: '', estado: 'pendiente',
});

function FormAccion({ a, set, ro, trabajadores }: { a: AccionEditable; set: (a: AccionEditable) => void; ro: boolean; trabajadores: PropsPaso5['trabajadores'] }) {
  return (
    <div className="pe2-grid">
      {!a.sourceKey && (
        <div className="ancho" style={{ gridColumn: '1 / -1' }}>
          <Campo etiqueta="Descripción">
            <textarea value={a.descripcion} disabled={ro} onChange={e => set({ ...a, descripcion: e.target.value })} />
          </Campo>
        </div>
      )}
      <Campo etiqueta={`Responsable (trabajador)${a.obligatoria ? ' *' : ''}`}>
        <select value={a.responsableId ?? ''} disabled={ro} onChange={e => set({ ...a, responsableId: e.target.value || null })}>
          <option value="">Sin trabajador</option>
          {trabajadores.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
        </select>
      </Campo>
      <Campo etiqueta="o cargo responsable" nota="Por ejemplo: jefe de bodega.">
        <input type="text" value={a.responsableCargo} disabled={ro} onChange={e => set({ ...a, responsableCargo: e.target.value })} />
      </Campo>
      <Campo etiqueta={`Fecha compromiso${a.obligatoria ? ' *' : ''}`}>
        <input type="date" value={a.fecha} disabled={ro} onChange={e => set({ ...a, fecha: e.target.value })} />
      </Campo>
      <Campo etiqueta="Estado">
        <select value={a.estado} disabled={ro} onChange={e => set({ ...a, estado: e.target.value as EstadoAccion })}>
          {ESTADOS_ACCION.map(s => <option key={s} value={s}>{ETIQUETA_ESTADO_ACCION[s]}</option>)}
        </select>
      </Campo>
    </div>
  );
}

function TarjetaAccion({ titulo, a, origen, resuelto, ro, puedeBorrar, trabajadores, guardando, onGuardar, onBorrar, evidencias }: {
  titulo: string; a: AccionEditable; origen?: string; resuelto?: boolean; ro: boolean; puedeBorrar: boolean;
  trabajadores: PropsPaso5['trabajadores']; guardando: boolean; onGuardar: (a: AccionEditable) => Promise<boolean>; onBorrar?: () => void;
  evidencias?: React.ReactNode;
}) {
  const [b, setB] = useState(a);
  const [errores, setErrores] = useState<string[]>([]);
  // La evidencia se sube fuera de la tarjeta (PE-A4b): conEvidencia sale siempre de `a`, no del estado del montaje,
  // y el aviso de cierre sin evidencia se va apenas llega una.
  useEffect(() => {
    if (a.conEvidencia) setErrores(es => es.filter(x => x !== MENSAJE_CIERRE_SIN_EVIDENCIA));
  }, [a.conEvidencia]);
  const guardar = async () => {
    const actual = { ...b, conEvidencia: a.conEvidencia };
    const e = validarAccion(actual);
    if (!a.id && b.obligatoria && b.estado === 'cerrada') e.push('Guárdala primero sin cerrar; después adjunta la evidencia.');
    setErrores(e);
    if (e.length === 0) await onGuardar(actual);
  };
  return (
    <div className="pe2-card pe2-sub-card">
      <div className="pe2-cabecera">
        <b>{titulo}</b>
        <div className="pe2-chips">
          {a.obligatoria && <span className="pe2-semaforo" style={{ color: '#ef4444', borderColor: '#ef4444' }}>Obligatoria</span>}
          {resuelto && <span className="pe2-semaforo" style={{ color: '#22c55e', borderColor: '#22c55e' }}>✓ Origen resuelto</span>}
          <span className="pe2-semaforo" style={{ color: '#38bdf8', borderColor: '#38bdf8' }} title="Implementación del plan de emergencia in situ">Evidencia {ITEM_PO18}</span>
        </div>
      </div>
      {a.sourceKey && <p className="pe2-nota" style={{ margin: 0 }}>{a.descripcion}</p>}
      {origen && <small className="pe2-fuente-doc">Origen: {origen}</small>}
      <FormAccion a={b} set={setB} ro={ro} trabajadores={trabajadores} />
      {evidencias}
      {errores.length > 0 && <div className="pe2-error" role="alert">{errores.join(' ')}</div>}
      {(!ro || (puedeBorrar && onBorrar)) && (
        <div className="pe2-acciones">
          {puedeBorrar && onBorrar && <button type="button" className="pe2-btn peligro" onClick={onBorrar}>Borrar</button>}
          {!ro && <button type="button" className="pe2-btn primario" disabled={guardando} onClick={guardar}>{a.id ? 'Guardar cambios' : 'Guardar acción'}</button>}
        </div>
      )}
    </div>
  );
}

export default function Paso5Acciones(p: PropsPaso5) {
  const sinc = sincronizarAcciones(p.acciones, p.derivadas);
  const vigentes = new Set(p.derivadas.map(d => d.sourceKey));
  const [manual, setManual] = useState<AccionEditable | null>(null);
  const origen = (a: { source_method?: string | null; norma?: string | null; articulo?: string | null; sourceMethod?: string | null }) =>
    [a.source_method ?? a.sourceMethod, a.norma, a.articulo].filter(Boolean).join(' · ');
  return (
    <div className="pe2-columna">
      <section className="pe2-card" aria-label="Acciones obligatorias por guardar">
        <h3>Obligatorias por guardar ({sinc.propuestas.length})</h3>
        <p className="pe2-nota" style={{ margin: 0 }}>Salen de los cálculos. Para guardarlas hacen falta responsable (trabajador o cargo) y fecha compromiso.</p>
        {sinc.propuestas.length === 0 && <p className="pe2-nota">No hay acciones obligatorias nuevas.</p>}
        {sinc.propuestas.map(d => (
          <TarjetaAccion key={d.sourceKey} titulo={d.articulo} a={editableDesdeDerivada(d)} origen={origen(d)} ro={p.ro} puedeBorrar={false}
            trabajadores={p.trabajadores} guardando={p.guardando} onGuardar={p.onGuardar} />
        ))}
      </section>

      <section className="pe2-card" aria-label="Plan de acción guardado">
        <h3>Plan de acción ({p.acciones.length})</h3>
        {p.acciones.length === 0 && <p className="pe2-nota">Aún no hay acciones guardadas.</p>}
        {p.errorEvidencias && <div className="pe2-error" role="alert">{p.errorEvidencias}</div>}
        {p.acciones.map(f => {
          const lista = p.evidencias.filter(e => e.action_id === f.id);
          const a = { ...editableDesdeFila(f), conEvidencia: lista.length > 0 || !!f.evidence_path?.trim() };
          return (
            <TarjetaAccion key={f.id} titulo={f.articulo ?? 'Acción manual'} a={a} origen={origen(f) || 'Manual'}
              resuelto={origenResuelto(f, vigentes)} ro={p.ro} puedeBorrar={p.puedeBorrar}
              trabajadores={p.trabajadores} guardando={p.guardando} onGuardar={p.onGuardar} onBorrar={() => p.onBorrar(f)}
              evidencias={<Evidencias fila={f} lista={lista} ro={p.ro} puedeBorrar={p.puedeBorrar} p={p} />} />
          );
        })}
      </section>

      {!p.ro && (
        <section className="pe2-card" aria-label="Acción manual">
          <h3>Acción manual</h3>
          {manual ? (
            <TarjetaAccion titulo="Nueva acción" a={manual} ro={p.ro} puedeBorrar={false} trabajadores={p.trabajadores} guardando={p.guardando}
              onGuardar={async a => { const ok = await p.onGuardar(a); if (ok) setManual(null); return ok; }} />
          ) : (
            <div className="pe2-acciones"><button type="button" className="pe2-btn" onClick={() => setManual(nuevaManual())}>Agregar acción manual</button></div>
          )}
        </section>
      )}
    </div>
  );
}
